import { useEffect, useState } from "react";
import { KeyRound, Loader2, X } from "lucide-react";

import { changePassword } from "../lib/api";

const inputClass =
  "w-full rounded-full border border-[#cccccc] bg-white px-3.5 py-2 text-sm text-[#323c44] focus:border-[#57c1a9] focus:outline-none";

/**
 * Change your own password.
 *
 * `forced` is for an account an admin has given a temporary password: the
 * dialog cannot be dismissed until the user has chosen their own.
 */
export default function ChangePasswordDialog({ open, forced = false, onClose, onChanged }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [signOutOthers, setSignOutOthers] = useState(true);
  const [doneMessage, setDoneMessage] = useState("");

  useEffect(() => {
    if (open) {
      setCurrent("");
      setNext("");
      setConfirm("");
      setError("");
      setDone(false);
      setSignOutOthers(true);
    }
  }, [open]);

  if (!open) return null;

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    if (next.length < 8) {
      setError("Your new password must be at least 8 characters");
      return;
    }
    if (next !== confirm) {
      setError("The two new passwords do not match");
      return;
    }
    setSaving(true);
    try {
      const result = await changePassword(current, next, signOutOthers);
      setDoneMessage(result?.message || "Your password has been changed.");
      setDone(true);
      if (onChanged) onChanged();
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not change your password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="change-password-dialog">
      <div className="absolute inset-0 bg-[#252832]/60" onClick={forced ? undefined : onClose} />
      <div className="relative w-full max-w-md rounded-[14px] bg-white p-6 shadow-float">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#e8faf5]">
              <KeyRound className="h-5 w-5 text-[#119996]" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-[#323c44]">
                {forced ? "Choose your own password" : "Change password"}
              </h2>
              <p className="text-sm text-[#4a5b68]">
                {forced
                  ? "An admin set a temporary password for you. Choose your own to carry on."
                  : "At least 8 characters."}
              </p>
            </div>
          </div>
          {!forced && (
            <button
              type="button"
              onClick={onClose}
              className="rounded p-1 text-[#7d8b96] hover:text-[#323c44]"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>

        {done ? (
          <div className="space-y-4">
            <p className="rounded-lg bg-[#e8faf5] px-3 py-2 text-sm text-[#1f5d74]">
              {doneMessage || "Your password has been changed."}
            </p>
            <button
              type="button"
              onClick={onClose}
              className="w-full rounded-full bg-[#50e3c2] px-4 py-2 text-sm font-bold uppercase tracking-[0.12em] text-[#323c44] hover:bg-[#57c1a9]"
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <label className="block">
              <span className="mb-1 block text-xs font-semibold text-[#4a5b68]">
                {forced ? "Temporary password" : "Current password"}
              </span>
              <input
                type="password"
                required
                autoComplete="current-password"
                className={inputClass}
                value={current}
                onChange={(event) => setCurrent(event.target.value)}
                data-testid="current-password-input"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-semibold text-[#4a5b68]">New password</span>
              <input
                type="password"
                required
                minLength={8}
                autoComplete="new-password"
                className={inputClass}
                value={next}
                onChange={(event) => setNext(event.target.value)}
                data-testid="new-password-input"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-semibold text-[#4a5b68]">Confirm new password</span>
              <input
                type="password"
                required
                minLength={8}
                autoComplete="new-password"
                className={inputClass}
                value={confirm}
                onChange={(event) => setConfirm(event.target.value)}
                data-testid="confirm-password-input"
              />
            </label>
            <label className="flex items-start gap-2 pt-1 text-sm text-[#323c44]">
              <input
                type="checkbox"
                checked={signOutOthers}
                onChange={(event) => setSignOutOthers(event.target.checked)}
                className="mt-0.5 accent-[#57c1a9]"
                data-testid="sign-out-others"
              />
              <span>
                Sign out of all other devices
                <span className="block text-[11px] text-[#7d8b96]">
                  Recommended. You stay signed in here.
                </span>
              </span>
            </label>
            {error && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>}
            <button
              type="submit"
              disabled={saving}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#50e3c2] px-4 py-2 text-sm font-bold uppercase tracking-[0.12em] text-[#323c44] hover:bg-[#57c1a9] disabled:opacity-50"
              data-testid="change-password-submit"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              Change password
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
